import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { api, clinicHeader, readError } from "../api/client";
import type { ClinicRole } from "../api/session";
import { ErrorNote } from "../components/ErrorNote";

type Clinic = {
  id: string;
  name: string;
  code?: string;
  address?: string;
  phone?: string;
  timezone?: string;
  staff_count?: number;
};

type ClinicStaff = {
  staff_id: string;
  email: string;
  full_name: string;
  role: string;
  is_org_admin?: boolean;
};

const ROLES: { value: ClinicRole; label: string }[] = [
  { value: "doctor", label: "Doctor" },
  { value: "nurse", label: "Nurse" },
  { value: "front_desk", label: "Front desk" },
  { value: "clinic_admin", label: "Clinic admin" },
];

const ROLE_LABELS: Record<string, string> = Object.fromEntries(
  ROLES.map((r) => [r.value, r.label]),
);

/**
 * The organisation's own back office (IDN-F-040).
 *
 * Django admin did this until now, and IDN-F-046 allows that where a custom
 * surface is not warranted. Opening a clinic is the one place it is: every
 * clinic that ever joins passes through here, it is the first thing a
 * prospective customer is shown, and it ends at a poster somebody prints and
 * puts on a wall. A table of model rows is the wrong shape for all three.
 *
 * Authority is not decided here. The service refuses anybody who is not an
 * organisation admin (ADR-008), and this screen reads that refusal and says
 * so rather than hiding a button and pretending the rule is a preference.
 */
export function Organisation() {
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [staff, setStaff] = useState<Record<string, ClinicStaff[]>>({});
  const [open, setOpen] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refused, setRefused] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [making, setMaking] = useState(false);

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<ClinicRole>("doctor");
  const [adding, setAdding] = useState(false);
  /**
   * Shown only once the address turns out to be new.
   *
   * Asking for a name and a password up front makes adding an existing
   * colleague to a second clinic look like hiring them twice. The server
   * refuses an unknown address without them, and that refusal is what
   * opens these two fields.
   */
  const [fullName, setFullName] = useState("");
  const [isNew, setIsNew] = useState(false);

  /**
   * Correcting a clinic, and correcting a colleague's name.
   *
   * A clinic opened on a Tuesday gets its address on a Wednesday. Until
   * these existed the only way to do that was Django admin, which is a table
   * of model rows and the wrong shape for somebody fixing a telephone number.
   */
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState({ name: "", phone: "", address: "" });
  const [saving, setSaving] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameTo, setRenameTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: failed, response } = await api.GET("/admin/clinics", {
      params: { header: clinicHeader() },
    });
    setLoading(false);

    if (response?.status === 403) {
      setRefused(true);
      return;
    }
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setClinics((data?.items ?? []) as Clinic[]);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function loadStaff(clinicId: string) {
    const { data, error: failed } = await api.GET("/admin/clinics/{id}/staff", {
      params: { path: { id: clinicId }, header: clinicHeader() },
    });
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setStaff((held) => ({ ...held, [clinicId]: (data?.items ?? []) as ClinicStaff[] }));
  }

  function toggle(clinicId: string) {
    const next = open === clinicId ? null : clinicId;
    setOpen(next);
    if (next && !staff[next]) void loadStaff(next);
  }

  async function createClinic(event: React.FormEvent) {
    event.preventDefault();
    setMaking(true);
    setError(null);

    const { error: failed } = await api.POST("/admin/clinics", {
      params: { header: clinicHeader() },
      body: { name: name.trim(), phone: phone.trim(), address: address.trim() },
    });
    setMaking(false);

    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setName("");
    setPhone("");
    setAddress("");
    await load();
  }

  async function addStaff(event: React.FormEvent, clinicId: string) {
    event.preventDefault();
    setAdding(true);
    setError(null);

    const { error: failed } = await api.POST("/admin/clinics/{id}/staff", {
      params: { path: { id: clinicId }, header: clinicHeader() },
      body: {
        email: email.trim(),
        role,
        ...(isNew ? { full_name: fullName.trim() } : {}),
      },
    });
    setAdding(false);

    if (failed) {
      const message = readError(failed).message;
      // The server says an unknown address needs a name and a password. That
      // is the moment to ask for them, rather than asking everybody always.
      //
      // It arrives as a 400, but it is not a failure to report: the form is
      // asking for more, and the next two fields are the answer. Raising it
      // as an error put a red banner at the top of the screen while the
      // thing to do sat at the bottom, which reads as a fault and points
      // away from the fix. So it opens the fields and says so beside them.
      if (/give their name/i.test(message)) {
        setIsNew(true);
        return;
      }
      setError(message);
      return;
    }
    setEmail("");
    setFullName("");
    setIsNew(false);
    await loadStaff(clinicId);
    await load();
  }

  function startEditing(clinic: Clinic) {
    setError(null);
    setEditing(clinic.id);
    setDraft({
      name: clinic.name,
      phone: clinic.phone ?? "",
      address: clinic.address ?? "",
    });
  }

  async function saveClinic(event: React.FormEvent, clinicId: string) {
    event.preventDefault();
    setSaving(true);
    setError(null);

    const { error: failed } = await api.PATCH("/admin/clinics/{id}", {
      params: { path: { id: clinicId }, header: clinicHeader() },
      body: {
        name: draft.name.trim(),
        phone: draft.phone.trim(),
        address: draft.address.trim(),
      },
    });
    setSaving(false);

    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setEditing(null);
    await load();
  }

  async function saveName(event: React.FormEvent, staffId: string, clinicId: string) {
    event.preventDefault();
    setSaving(true);
    setError(null);

    const { error: failed } = await api.PATCH("/admin/staff/{id}", {
      params: { path: { id: staffId }, header: clinicHeader() },
      body: { full_name: renameTo.trim() },
    });
    setSaving(false);

    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setRenaming(null);
    await loadStaff(clinicId);
  }

  if (refused) {
    return (
      <section>
        <h1 className="screen-head">Your organisation</h1>
        <p className="screen-lead">
          This is for organisation administrators. Running a clinic and opening
          one are different jobs, so the people who do the first are not
          automatically given the second.
        </p>
      </section>
    );
  }

  return (
    <section>
      <h1 className="screen-head">Your organisation</h1>
      <p className="screen-lead">
        The clinics you run, who works at each, and where a new one starts. A
        clinic is ready to take patients the moment it exists: its code and its
        poster are made with it.
      </p>

      <ErrorNote message={error} />

      <form className="org-new" onSubmit={createClinic}>
        <h2>Open a clinic</h2>
        <div className="org-new-fields">
          <label>
            <span>Name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Axon Mont Kiara"
              required
            />
          </label>
          <label>
            <span>Telephone</span>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+60 3-6201 1234"
            />
          </label>
          <label className="wide">
            <span>Address</span>
            <input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="1 Jalan Kiara, 50480 Kuala Lumpur"
            />
          </label>
        </div>
        <button type="submit" disabled={making || !name.trim()}>
          {making ? "Opening…" : "Open the clinic"}
        </button>
      </form>

      {loading ? (
        <p className="empty">Reading your organisation…</p>
      ) : clinics.length === 0 ? (
        <p className="empty">No clinics yet. The form above makes the first one.</p>
      ) : (
        <ul className="org-list">
          {clinics.map((clinic) => {
            const showing = open === clinic.id;
            const people = staff[clinic.id];
            return (
              <li key={clinic.id} className="org-clinic">
                <div className="org-clinic-head">
                  <div>
                    <h2>{clinic.name}</h2>
                    <p className="muted">
                      {clinic.staff_count ?? 0}{" "}
                      {clinic.staff_count === 1 ? "person" : "people"}
                      {clinic.address ? ` · ${clinic.address}` : ""}
                    </p>
                  </div>
                  <div className="org-clinic-actions">
                    <Link to="/poster" className="link">
                      Its poster
                    </Link>
                    <button
                      type="button"
                      className="link"
                      onClick={() =>
                        editing === clinic.id ? setEditing(null) : startEditing(clinic)
                      }
                    >
                      {editing === clinic.id ? "Leave it" : "Its details"}
                    </button>
                    <button type="button" className="link" onClick={() => toggle(clinic.id)}>
                      {showing ? "Hide staff" : "Who works here"}
                    </button>
                  </div>
                </div>

                {editing === clinic.id && (
                  <form className="org-edit" onSubmit={(e) => saveClinic(e, clinic.id)}>
                    <label>
                      <span>Name</span>
                      <input
                        value={draft.name}
                        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                        required
                      />
                    </label>
                    <label>
                      <span>Telephone</span>
                      <input
                        value={draft.phone}
                        onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
                        placeholder="+60 3-6201 1234"
                      />
                    </label>
                    <label className="wide">
                      <span>Address</span>
                      <input
                        value={draft.address}
                        onChange={(e) => setDraft({ ...draft, address: e.target.value })}
                        placeholder="1 Jalan Kiara, 50480 Kuala Lumpur"
                      />
                    </label>
                    <p className="org-edit-note">
                      The clinic&rsquo;s code is not here. It is printed on the
                      poster and inside the QR, so changing it would break
                      every one already on a wall.
                    </p>
                    <button type="submit" disabled={saving || !draft.name.trim()}>
                      {saving ? "Saving…" : "Save"}
                    </button>
                  </form>
                )}

                {showing && (
                  <div className="org-staff">
                    {people === undefined ? (
                      <p className="empty">Reading…</p>
                    ) : people.length === 0 ? (
                      <p className="empty">Nobody is assigned here yet.</p>
                    ) : (
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Role here</th>
                          </tr>
                        </thead>
                        <tbody>
                          {people.map((person) => (
                            <tr key={person.staff_id}>
                              <td>
                                {renaming === person.staff_id ? (
                                  <form
                                    className="org-rename"
                                    onSubmit={(e) =>
                                      saveName(e, person.staff_id, clinic.id)
                                    }
                                  >
                                    <input
                                      value={renameTo}
                                      onChange={(e) => setRenameTo(e.target.value)}
                                      aria-label="Their name"
                                      autoFocus
                                      required
                                    />
                                    <button type="submit" disabled={saving}>
                                      {saving ? "Saving…" : "Save"}
                                    </button>
                                    <button
                                      type="button"
                                      className="link"
                                      onClick={() => setRenaming(null)}
                                    >
                                      Leave it
                                    </button>
                                  </form>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      className="link org-name"
                                      onClick={() => {
                                        setError(null);
                                        setRenaming(person.staff_id);
                                        setRenameTo(person.full_name);
                                      }}
                                      title="Correct this name"
                                    >
                                      {person.full_name}
                                    </button>
                                    {person.is_org_admin && (
                                      <span className="tag">Organisation admin</span>
                                    )}
                                  </>
                                )}
                              </td>
                              <td className="muted">{person.email}</td>
                              <td>{ROLE_LABELS[person.role] ?? person.role}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    <form className="org-add" onSubmit={(e) => addStaff(e, clinic.id)}>
                      {isNew && (
                        <p className="org-asking">
                          Nobody in this organisation uses that address yet.
                          Give their name and a first password, and they are
                          hired into this clinic.
                        </p>
                      )}
                      <label>
                        <span>{isNew ? "Adding a new colleague" : "Add somebody by email"}</span>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="name@yourclinic.com"
                          required
                        />
                      </label>
                      {isNew && (
                        <>
                          <label>
                            <span>Their name</span>
                            <input
                              value={fullName}
                              onChange={(e) => setFullName(e.target.value)}
                              placeholder="Dr Nur Aisyah"
                              required
                            />
                          </label>
                        </>
                      )}
                      <label>
                        <span>As</span>
                        <select
                          value={role}
                          onChange={(e) => setRole(e.target.value as ClinicRole)}
                        >
                          {ROLES.map((r) => (
                            <option key={r.value} value={r.value}>
                              {r.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <button type="submit" disabled={adding || !email.trim()}>
                        {adding ? "Adding…" : "Add"}
                      </button>
                    </form>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
